import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { postService, commentService, Post, Comment, hashtagService } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import EditPostModal from "../components/EditPostModal";
import DeletePostButton from "../components/DeletePostButton";
import CommentItem from "../components/CommentItem";
import { Eye, MessageCircle, Star, Send, Edit } from "lucide-react";
import { FaXTwitter, FaFacebook, FaWhatsapp } from "react-icons/fa6";
import { formatDistanceToNow } from "date-fns";
import { enGB } from "date-fns/locale";
import toast from "react-hot-toast";

const XTwitterIcon = FaXTwitter as any;
const FacebookIcon = FaFacebook as any;
const WhatsappIcon = FaWhatsapp as any;

const PostDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [userRating, setUserRating] = useState<number>(0);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [rankingPosts, setRankingPosts] = useState<Post[]>([]);
  const shareUrl = typeof window !== "undefined" && id ? `${window.location.origin}/post/${id}` : "";

  useEffect(() => {
    if (id) {
      loadPost();
      loadComments();
    }
  }, [id]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [frontpageData, recentData, hashtagsData, ranksResp] = await Promise.all([
          postService.getFrontpage(),
          postService.getPosts(0, 10),
          hashtagService.getPopularHashtags(),
          postService.getRanks(),
        ]);
        setRankingPosts(ranksResp.posts);
      } catch (error) {
        toast.error("Error loading homepage");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const loadPost = async () => {
    try {
      if (id) {
        const postData = await postService.getPost(id);
        setPost(postData);
      }
    } catch (error) {
      toast.error("Error loading article");
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      if (id) {
        const commentsData = await commentService.getComments(id);
        setComments(commentsData);
      }
    } catch (error) {
      toast.error("Error loading comments");
    }
  };

  const handleRate = async (rating: number) => {
    if (!user || !post) {
      toast.error("You must sign in to rate articles");
      return;
    }

    // ✅ NUEVA VALIDACIÓN: Verificar si es el autor
    if (user.username === post.author_username) {
      toast.error("You cannot rate your own articles");
      return;
    }

    if (submittingRating) return;

    setSubmittingRating(true);
    try {
      await postService.ratePost(post.id, rating);
      setUserRating(rating);

      // Reload post to get updated rating
      await loadPost();

      toast.success("Rating submitted!");
    } catch (error: any) {
      // ✅ Manejar error específico del backend
      const errorMessage = error.response?.data?.detail || "Error submitting rating";
      toast.error(errorMessage);
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleComment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user || !post) {
      toast.error("You must sign in to comment");
      return;
    }

    if (!commentText.trim()) {
      toast.error("Write a comment");
      return;
    }

    setSubmittingComment(true);
    try {
      const newComment = await commentService.createComment(post.id, commentText.trim());
      setComments([...comments, newComment]);
      setCommentText("");
      toast.success("Comment added");
    } catch (error) {
      toast.error("Error submitting comment");
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditSuccess = (updatedPost: Post) => {
    setPost(updatedPost);
    setEditingPost(null);
  };

  const handleDeletePost = async () => {
    if (!post) return;

    try {
      await postService.deletePost(post.id);
      toast.success("Article deleted successfully");
      navigate("/profile");
    } catch (error) {
      throw error; // Re-throw to be handled by DeletePostButton
    }
  };

  const handleUpdateComment = (updatedComment: Comment) => {
    setComments(comments.map((comment) => (comment.id === updatedComment.id ? updatedComment : comment)));
  };

  const handleDeleteComment = (commentId: string) => {
    setComments(comments.filter((comment) => comment.id !== commentId));
  };

  const truncateContent = (content: string, maxLength: number = 200) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <div className="beat-container">
        <div className="beat-card p-8 rounded-lg">
          <div className="beat-loading h-8 mb-4"></div>
          <div className="beat-loading h-4 mb-2"></div>
          <div className="beat-loading h-4 mb-8"></div>
          <div style={{ width: "100%", aspectRatio: "16/9", background: "#D9D9D9" }} className="beat-loading"></div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="beat-container">
        <div className="beat-error">
          <h2 className="text-lg font-semibold mb-2">Article not found</h2>
          <p>The article you're looking for doesn't exist or has been deleted.</p>
          <Link to="/" className="beat-button mt-4 inline-block">
            Back to homepage
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="beat-container w-full flex flex-col xl:flex-row xl:items-start xl:justify-between gap-[24px] sm:gap-[30px] xl:gap-[37px] px-4 sm:px-6 md:px-11 py-6 sm:py-8 xl:py-11">
      {/* Left column */}
      <div className="left-column lg:col-span-3 flex flex-col items-start gap-[20px] sm:gap-[24px] xl:gap-[26px] w-full">
        <div className="w-full flex flex-col justify-start items-start gap-[20px] sm:gap-[24px] xl:gap-[26px]">
          {/* Article Image */}
          {post.image && (
            <div style={{ width: "100%", aspectRatio: "16/9", background: "#D9D9D9" }}>
              <img
                src={post.image}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                className="beat-image-bw"
              />
            </div>
          )}
          {/* Author and metadata section */}
          <div className="Author-metadata-section flex flex-wrap items-center gap-2">
            {(() => {
              console.log("post: ", post);
              return null;
            })()}
            <Link to={`/voice/${post.author_username}`}>
              <div
                className="flex flex-row items-center gap-2"
                style={{
                  color: "#737373",
                  fontSize: "13px",
                  fontFamily: "Abhaya Libre",
                  fontWeight: "600",
                  lineHeight: "16px",
                  letterSpacing: 0.6,
                  wordWrap: "break-word",
                }}
              >
                <img
                  src={post.author_avatar || "/default-avatar.png"}
                  alt={post.author_username}
                  className="w-6 h-6 rounded-full object-cover"
                />
                By {post.author_username}
              </div>
            </Link>
            <div
              style={{
                width: "5px",
                height: "16px",
                color: "#737373",
                fontSize: "13px",
                fontFamily: "Inter",
                fontWeight: "400",
                lineHeight: "16px",
                letterSpacing: "0.60px",
                wordWrap: "break-word",
              }}
            >
              |
            </div>
            <div
              style={{
                color: "#737373",
                fontSize: "13px",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                lineHeight: "16px",
                letterSpacing: "0.60px",
                wordWrap: "break-word",
              }}
            >
              {formatDistanceToNow(new Date(post.created_at), {
                addSuffix: false,
                locale: enGB,
              })}
            </div>
            <div
              style={{
                width: "5px",
                height: "16px",
                color: "#737373",
                fontSize: "13px",
                fontFamily: "Instrument Sans",
                fontWeight: "400",
                lineHeight: "16px",
                letterSpacing: "0.60px",
                wordWrap: "break-word",
              }}
            >
              |
            </div>
            <div
              style={{
                color: "#737373",
                fontSize: "13px",
                fontFamily: "Abhaya Libre",
                fontWeight: "400",
                lineHeight: "16px",
                letterSpacing: "0.60px",
                wordWrap: "break-word",
              }}
            >
              {post.hashtags[0] || "General"}
            </div>
          </div>
          {/* Title */}
          <div
            className="text-3xl sm:text-4xl md:text-5xl xl:text-[52px] leading-tight xl:leading-[52px]"
            style={{
              alignSelf: "stretch",
              color: "#171717",
              fontFamily: "Abhaya Libre",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
          >
            {post.title}
          </div>
          {/* Content */}
          <div
            className="text-[15px] sm:text-[16px] leading-[24px] sm:leading-[26px]"
            style={{
              alignSelf: "stretch",
              color: "#404040",
              fontFamily: "Instrument Sans",
              fontWeight: "400",
              wordWrap: "break-word",
            }}
            dangerouslySetInnerHTML={{
              __html: (() => {
                const escape = (s: string) =>
                  s
                    .replace(/&/g, "&amp;")
                    .replace(/</g, "&lt;")
                    .replace(/>/g, "&gt;")
                    .replace(/\"/g, "&quot;")
                    .replace(/'/g, "&#39;");
                const bold = /\*\*(.*?)\*\*/g;
                const italicStar = /\*(.*?)\*/g;
                const italicUnderscore = /_(.*?)_/g;
                let html = escape(post.content);
                html = html.replace(bold, "<strong>$1</strong>");
                html = html.replace(italicUnderscore, "<em>$1</em>");
                html = html.replace(italicStar, "<em>$1</em>");
                html = html.replace(/\n/g, "<br />");
                return html;
              })(),
            }}
          />
          {/* Rating Button */}
          {user && user.username !== post.author_username && (
            <div
              style={{
                width: "150px",
                paddingLeft: "30px",
                paddingRight: "30px",
                paddingTop: "7px",
                paddingBottom: "7px",
                borderRadius: "37px",
                outline: "1px black solid",
                outlineOffset: "-1px",
                justifyContent: "center",
                alignItems: "center",
                gap: "10px",
                display: "inline-flex",
                cursor: "pointer",
              }}
              onClick={() => {
                // Toggle rating section visibility or handle rating
                const ratingSection = document.getElementById("rating-section");
                if (ratingSection) {
                  ratingSection.style.display = ratingSection.style.display === "none" ? "block" : "none";
                }
              }}
            >
              <div
                style={{
                  textAlign: "center",
                  color: "black",
                  fontSize: "14px",
                  fontFamily: "Abhaya Libre ExtraBold",
                  fontWeight: "800",
                  wordWrap: "break-word",
                }}
              >
                Rate post
              </div>
            </div>
          )}
          {/* Hidden Rating Section */}
          {user && user.username !== post.author_username && (
            <div id="rating-section" style={{ display: "none", marginTop: "20px" }}>
              <h3 style={{ fontSize: "18px", fontWeight: "600", marginBottom: "16px" }}>Rate this article</h3>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {[1, 2, 3, 4, 5].map((rating) => (
                  <button
                    key={rating}
                    onClick={() => handleRate(rating)}
                    disabled={submittingRating}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: rating <= userRating ? "#fbbf24" : "#d1d5db",
                      fontSize: "24px",
                    }}
                  >
                    ★
                  </button>
                ))}
                <span style={{ marginLeft: "12px", fontSize: "14px", color: "#6b7280" }}>
                  {userRating > 0 ? `Your rating: ${userRating}/5` : "Click on a star"}
                </span>
              </div>
            </div>
          )}
          {/* Hashtags */}
          <div className="w-full sm:w-[250px] border-t border-[#E5E5E5] flex flex-col justify-start items-start gap-2 py-4">
            <div className="w-full flex justify-start items-center gap-4">
              {post.hashtags.slice(0, 3).map((tag, index) => (
                <Link
                  key={tag}
                  to={`/thepulse?hashtag=${tag}`}
                  style={{
                    color: "#525252",
                    fontSize: "13px",
                    fontFamily: "Abhaya Libre",
                    fontWeight: "400",
                    wordWrap: "break-word",
                    textDecoration: "none",
                  }}
                >
                  #{tag}
                </Link>
              ))}
            </div>
          </div>
          {/* Share Buttons */}
          <div className="w-full border-t border-[#E5E5E5] flex items-center gap-4 py-4">
            <span
              style={{
                color: "#525252",
                fontSize: "14px",
                fontFamily: "Abhaya Libre",
                fontWeight: "600",
              }}
            >
              Share:
            </span>
            <div className="flex gap-4">
              {/* Twitter / X */}
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(
                  shareUrl,
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-black transition-colors"
                title="Share on X"
              >
                <XTwitterIcon size={20} />
              </a>
              {/* Facebook */}
              <a
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-blue-600 transition-colors"
                title="Share on Facebook"
              >
                <FacebookIcon size={20} />
              </a>
              {/* WhatsApp */}
              <a
                href={`https://wa.me/?text=${encodeURIComponent(`${post.title} ${shareUrl}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-green-600 transition-colors"
                title="Share on WhatsApp"
              >
                <WhatsappIcon size={20} />
              </a>
            </div>
          </div>
          {/* Edit/Delete buttons for author */}
          {user && user.username === post.author_username && (
            <div style={{ display: "flex", gap: "8px", marginTop: "20px" }}>
              <button
                onClick={() => setEditingPost(post)}
                style={{
                  padding: "8px",
                  background: "#f3f4f6",
                  border: "1px solid #d1d5db",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
                title="Edit article"
              >
                <Edit style={{ width: "20px", height: "20px" }} />
              </button>
              <DeletePostButton
                onConfirm={handleDeletePost}
                className="hover:bg-red-50 rounded p-2 transition-colors"
              />
            </div>
          )}
          {/* Stats */}
          <div style={{ display: "flex", gap: "16px", fontSize: "14px", color: "#6b7280", marginTop: "16px" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Eye style={{ width: "16px", height: "16px" }} />
              {post.visits} visits
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <Star style={{ width: "16px", height: "16px" }} />
              {post.average_rating.toFixed(1)} ({post.ratings_count})
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <MessageCircle style={{ width: "16px", height: "16px" }} />
              {post.comments_count} comments
            </span>
          </div>
          {/* Note for authors */}
          {user && user.username === post.author_username && (
            <div
              style={{
                marginTop: "32px",
                padding: "16px",
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
              }}
            >
              <p style={{ color: "#374151", fontSize: "14px", margin: "0" }}>
                <strong>💡 Note:</strong> You cannot rate your own articles. The Mojo system is based on community
                recognition.
              </p>
            </div>
          )}
        </div>

        {/* Comments Section */}
        <div className="beat-card w-full rounded-lg p-4 sm:p-6 md:p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-newspaper-900 mb-4 sm:mb-6 flex items-center">
            <MessageCircle className="w-6 h-6 mr-2" />
            Comments ({comments.length})
          </h2>

          {/* Add Comment Form */}
          {user ? (
            <form onSubmit={handleComment} className="mb-8">
              <textarea
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="beat-input min-h-[100px] resize-none rounded-lg"
                placeholder="Share your opinion about this article..."
                maxLength={1000}
              />
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mt-3">
                <span className="text-xs text-newspaper-500">{commentText.length}/1000 characters</span>
                <button
                  type="submit"
                  disabled={submittingComment || !commentText.trim()}
                  className="beat-button flex items-center space-x-2 rounded-lg"
                >
                  {submittingComment ? (
                    <div className="beat-spinner"></div>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Comment</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <div className="mb-8 p-4 bg-newspaper-100 border border-newspaper-200">
              <p className="text-newspaper-700">
                <Link to="/login" className="text-newspaper-900 font-medium hover:underline">
                  Sign in
                </Link>{" "}
                to join the conversation.
              </p>
            </div>
          )}

          {/* Comments List */}
          <div className="space-y-6">
            {comments.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-newspaper-600">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No comments yet. Be the first to comment!</p>
              </div>
            ) : (
              comments.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  onUpdate={handleUpdateComment}
                  onDelete={handleDeleteComment}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Right Column */}
      {/* The Beatline */}
      {rankingPosts.length > 0 && (
        <aside>
          <div
            className="top-ranking xl:w-[258px] w-full hidden xl:flex h-full xl:space-y-3 space-y-5"
            style={{
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div
              style={{
                alignSelf: "stretch",
                color: "#171717",
                fontSize: 24,
                fontFamily: "Playfair Display",
                fontWeight: "600",
                lineHeight: "31px",
                wordWrap: "break-word",
              }}
            >
              The Beatline
            </div>
            {rankingPosts.slice(0, 3).map((p, idx) => (
              <div
                key={p.id}
                data-property-1="Default"
                style={{
                  alignSelf: "stretch",
                  background: "rgba(0, 0, 0, 0)",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "flex-start",
                  gap: 8,
                  display: "flex",
                }}
              >
                <div
                  className="top-ranking-item-tittle"
                  style={{
                    width: 257,
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    gap: 8,
                    display: "inline-flex",
                  }}
                >
                  <div
                    style={{
                      width: 15,
                      color: "black",
                      fontSize: "15px",
                      fontFamily: "Instrument Sans",
                      fontWeight: "700",
                      lineHeight: "25px",
                      wordWrap: "break-word",
                    }}
                  >
                    {idx + 1}
                    <br />-
                  </div>
                  <div
                    style={{
                      width: 234,
                      color: "#262626",
                      fontSize: "18px",
                      fontFamily: "Abhaya Libre",
                      fontWeight: "700",
                      lineHeight: "25px",
                      wordWrap: "break-word",
                    }}
                  >
                    <Link
                      to={`/post/${p.id}`}
                      style={{
                        color: "#262626",
                        textDecoration: "none",
                      }}
                      // onMouseEnter={(e) => e.target.style.textDecoration = "underline"}
                      // onMouseLeave={(e) => e.target.style.textDecoration = "none"}
                    >
                      {p.title}
                    </Link>
                  </div>
                </div>
                <div
                  className="top-ranking-item-content"
                  style={{
                    alignSelf: "stretch",
                    color: "#525252",
                    fontSize: "14px",
                    fontFamily: "Instrument Sans",
                    fontWeight: "400",
                    lineHeight: "20px",
                    wordWrap: "break-word",
                  }}
                >
                  {truncateContent(p.content, 90)}
                </div>
                <div
                  className="top-ranking-item-meta"
                  style={{
                    alignSelf: "stretch",
                    background: "rgba(0, 0, 0, 0)",
                    justifyContent: "space-between",
                    alignItems: "center",
                    display: "inline-flex",
                  }}
                >
                  <Link to={`/voice/${p.author_username}`}>
                    <div
                      className="flex flex-row items-center gap-2"
                      style={{
                        color: "#737373",
                        fontSize: "13px",
                        fontFamily: "Instrument Sans",
                        fontWeight: "400",
                        lineHeight: "16px",
                        letterSpacing: 0.6,
                        wordWrap: "break-word",
                      }}
                    >
                      <img
                        src={p.author_avatar || "/default-avatar.png"}
                        alt={p.author_username}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      By {p.author_username}
                    </div>
                  </Link>

                  <div className="inline-flex">
                    <Star className="w-4 h-4 mr-2 inline" />
                    <div
                      style={{
                        width: 20,
                        height: 16,
                        color: "#737373",
                        fontSize: "13px",
                        fontFamily: "Instrument Sans",
                        fontWeight: "400",
                        lineHeight: "16px",
                        letterSpacing: 0.6,
                        wordWrap: "break-word",
                      }}
                    >
                      {p.average_rating.toFixed(1)}
                    </div>
                  </div>
                </div>
                <div
                  className="top-ranking-item-hashtags"
                  style={{
                    alignSelf: "stretch",
                    background: "rgba(0, 0, 0, 0)",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: 11,
                    display: "inline-flex",
                  }}
                >
                  {p.hashtags.slice(0, 3).map((hashtag, hashIdx) => (
                    <div
                      key={hashIdx}
                      style={{
                        color: "#A3A3A3",
                        fontSize: "13px",
                        fontFamily: "Abhaya Libre",
                        fontWeight: "400",
                        lineHeight: "16px",
                        wordWrap: "break-word",
                      }}
                    >
                      #{hashtag}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </aside>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <EditPostModal
          post={editingPost}
          isOpen={!!editingPost}
          onClose={() => setEditingPost(null)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default PostDetail;
